import { type DuelStatus, otherSeat, type Seat } from '../duel/protocol'

export const C4_COLS = 7
export const C4_ROWS = 6
export const C4_CELLS = C4_COLS * C4_ROWS
export const C4_CONNECT = 4

/** Row-major from the bottom: index = row * C4_COLS + col, row 0 is where discs land first. */
export type C4Board = readonly (Seat | null)[]
export type C4Line = readonly [number, number, number, number]

export interface C4State {
  readonly board: C4Board
  /** Discs already in each column. */
  readonly heights: readonly number[]
  readonly turn: Seat
  readonly status: DuelStatus
  readonly winner: Seat | null
  readonly winLine: C4Line | null
  readonly moveCount: number
  /** Column of the last disc, for the UI to highlight. */
  readonly lastCol: number | null
}

export type C4MoveError = 'GAME_OVER' | 'NOT_YOUR_TURN' | 'INVALID_COLUMN' | 'COLUMN_FULL'

export type C4MoveResult =
  | { ok: true; state: C4State; row: number }
  | { ok: false; error: C4MoveError }

export function c4Index(row: number, col: number): number {
  return row * C4_COLS + col
}

export function isColumn(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < C4_COLS
}

export function createC4(firstTurn: Seat = 'X'): C4State {
  return {
    board: Array.from({ length: C4_CELLS }, () => null),
    heights: Array.from({ length: C4_COLS }, () => 0),
    turn: firstTurn,
    status: 'playing',
    winner: null,
    winLine: null,
    moveCount: 0,
    lastCol: null,
  }
}

export function legalColumns(state: C4State): number[] {
  const out: number[] = []
  for (let c = 0; c < C4_COLS; c++) if (state.heights[c]! < C4_ROWS) out.push(c)
  return out
}

const DIRECTIONS: readonly [number, number][] = [
  [0, 1], // horizontal
  [1, 0], // vertical
  [1, 1], // diagonal up-right
  [1, -1], // diagonal up-left
]

/** Four in a row through the disc at (row, col), if any. Only the last disc can complete a line. */
export function findC4Line(board: C4Board, row: number, col: number, seat: Seat): C4Line | null {
  for (const [dr, dc] of DIRECTIONS) {
    const cells = [c4Index(row, col)]
    for (const sign of [1, -1]) {
      let r = row + dr * sign
      let c = col + dc * sign
      while (r >= 0 && r < C4_ROWS && c >= 0 && c < C4_COLS && board[c4Index(r, c)] === seat) {
        cells.push(c4Index(r, c))
        r += dr * sign
        c += dc * sign
      }
    }
    if (cells.length >= C4_CONNECT) {
      cells.sort((a, b) => a - b)
      return [cells[0]!, cells[1]!, cells[2]!, cells[3]!]
    }
  }
  return null
}

export function dropDisc(state: C4State, seat: Seat, col: number): C4MoveResult {
  if (state.status !== 'playing') return { ok: false, error: 'GAME_OVER' }
  if (seat !== state.turn) return { ok: false, error: 'NOT_YOUR_TURN' }
  if (!isColumn(col)) return { ok: false, error: 'INVALID_COLUMN' }
  const row = state.heights[col]!
  if (row >= C4_ROWS) return { ok: false, error: 'COLUMN_FULL' }

  const board = [...state.board]
  board[c4Index(row, col)] = seat
  const heights = [...state.heights]
  heights[col] = row + 1
  const winLine = findC4Line(board, row, col, seat)
  const moveCount = state.moveCount + 1
  const full = moveCount === C4_CELLS
  const next: C4State = {
    board,
    heights,
    turn: otherSeat(seat),
    status: winLine ? 'won' : full ? 'draw' : 'playing',
    winner: winLine ? seat : null,
    winLine,
    moveCount,
    lastCol: col,
  }
  return { ok: true, state: next, row }
}

/** Ends the game with `loser` forfeiting (timeout or abandonment). */
export function forfeitC4(state: C4State, loser: Seat): C4State {
  if (state.status !== 'playing') return state
  return { ...state, status: 'forfeit', winner: otherSeat(loser), winLine: null }
}
